import {
  AfterViewInit,
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  Params,
  Router,
  RouterOutlet,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, Subscription, take, takeUntil, Subject } from 'rxjs';
import { LanguageService, SwarmLoginService } from './services';
import { CookieConsentService } from './services/cookie-consent.service';
import { FooterComponent } from './shared/components';
import { CookieComponent } from './shared/components/cookie/cookie.component';
import { NavbarComponent } from './shared/components/navbar/navbar.component';
import { CountriesActions } from './store/countries/countries.actions';
import { MeActions } from './store/me/me.actions';
import { CurrencyType } from './services/gift-cards-plans.service';
import { selectCountries } from './store/countries/countries.selector';
import { Country, IconComponent } from '@kinomap-dev/ng-ui-lib';
import { selectProfileData } from './store/me/me.selectors';
import { User } from './models';
import { SubscriptionsApiActions } from './modules/dashboard/store/subscriptions/subscriptions.actions';
import { ToasterComponent } from './shared/components/toaster/toaster.component';
import { ModalService } from './services/modal.service';
import { ModalComponent } from './shared/components/modal/modal.component';
import { TagStore } from './modules/browse/store/tag/tag.store';

declare let gtag: Function;

@Component({
  imports: [
    ToasterComponent,
    NavbarComponent,
    CookieComponent,
    FooterComponent,
    RouterOutlet,
    ModalComponent,
    IconComponent,
    TranslateModule,
  ],
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styles: `
    .router-outlet-container {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      flex: 1;
    }
    .font-loading {
      font-display: swap;
    }
  `,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public translate: TranslateService = inject(TranslateService);
  private languageService: LanguageService = inject(LanguageService);
  private router: Router = inject(Router);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private title: Title = inject(Title);
  private cookieService: CookieConsentService = inject(CookieConsentService);
  private store: Store = inject(Store);
  private loginService: SwarmLoginService = inject(SwarmLoginService);
  private tagStore: TagStore = inject(TagStore);
  public modalService: ModalService = inject(ModalService);

  private readonly destroy$ = new Subject<void>();
  private currentCurrency: CurrencyType | null = null;
  activeTracking: string | undefined;
  cookieControls: boolean;
  excludedCookiesRoutes: string[] = ['/cookies', '/terms', '/privacy'];
  excludedRoutesFooter: string[] = ['/login', '/register', '/404'];
  excludedRoutesNavbar: string[] = ['/register', '/event'];
  navigationControls: boolean;
  navigationControlsFooter: boolean;
  navigationControlsNavBar: boolean;
  subscription: Subscription[] = [];
  titleName: string;

  // FAKE ZENDESK MENU
  fakeZendeskMenu: boolean = false;
  fakeZendeskMenuClosing: boolean = false;

  // private addGoogleMapsScript(): void {
  //   const script: HTMLScriptElement = document.createElement('script');
  //   script.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${environment.google.googlePlacesApiKey}`;
  //   script.async = true;
  //   document.body.appendChild(script);
  // }

  private initializeStore(): void {
    this.store.dispatch(CountriesActions.getCountries());

    if (localStorage.getItem('user_access_token')) {
      this.store.dispatch(MeActions.getProfile());
    }

    this.store
      .select(selectProfileData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        if (user) {
          this.store.dispatch(MeActions.getSettings());
          this.store.dispatch(
            SubscriptionsApiActions.fetchActiveSubscription()
          );
          this.tagStore.getTags();
        }
      });

    // Met la currency initiale au chargement des pays
    this.store
      .select(selectCountries)
      .pipe(
        filter((countries: Country[]) => countries?.length > 0),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((countries: Country[]) => {
        this.updateCurrencyFromLanguage(countries);
      });

    // Change la currency à chaque changement de langue
    this.languageService.langEmitter
      .pipe(
        filter(() => this.currentCurrency !== null),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.store
          .select(selectCountries)
          .pipe(
            filter((countries: Country[]) => countries?.length > 0),
            take(1)
          )
          .subscribe((countries: Country[]) => {
            this.updateCurrencyFromLanguage(countries);
          });
      });
  }

  private updateCurrencyFromLanguage(countries: Country[]): void {
    const localLanguage: string = localStorage.getItem('lang');
    const lang: string = localLanguage?.split('-')[1]?.toLocaleLowerCase();
    const currency: CurrencyType =
      (countries.find((country: Country) => {
        return country.code === lang;
      })?.currency as CurrencyType) || 'eur';

    if (this.currentCurrency !== currency) {
      this.currentCurrency = currency;
      this.store.dispatch(MeActions.getCurrency({ currency }));
    }
  }

  private initializeCookieConsent(): void {
    this.activeTracking = this.cookieService.get();
    this.cookieService.cookieConsentEmitter
      .pipe(takeUntil(this.destroy$))
      .subscribe((el: string): void => {
        this.activeTracking = el;
      });
  }

  private extractTokenFromHash(): string | null {
    const hash: string = window.location.hash;
    if (hash && hash.includes('?token=')) {
      const paramsString: string = hash.split('?')[1];
      if (paramsString) {
        const params: URLSearchParams = new URLSearchParams(paramsString);
        return params.get('token');
      }
    }
    return null;
  }

  getChild(activatedRoute: ActivatedRoute): ActivatedRoute {
    if (activatedRoute.firstChild) {
      return this.getChild(activatedRoute.firstChild);
    }

    return activatedRoute;
  }

  ngAfterViewInit(): void {
    this.router.events
      .pipe(
        filter((event: NavigationEnd) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.titleName = this.getChild(this.activatedRoute).snapshot.data.title;
        this.titleName
          ? this.titleName
          : (this.titleName = 'default.head.title');
        this.translate
          .stream(this.titleName)
          .pipe(takeUntil(this.destroy$))
          .subscribe((translateTitle) => {
            this.titleName !== translateTitle
              ? this.title.setTitle(translateTitle)
              : false;
          });
        this.navigationControlsNavBar = this.excludedRoutesNavbar.find(
          (el: string): boolean => el === this.router.url
        )
          ? false
          : true;
        this.navigationControlsFooter = this.excludedRoutesFooter.find(
          (el: string): boolean => el === this.router.url
        )
          ? false
          : true;
        this.cookieControls = this.excludedCookiesRoutes.find(
          (el: string): boolean => el === event.url
        )
          ? false
          : true;

        if (this.activeTracking === 'allow') {
          gtag('js', new Date());
          gtag('config', 'UA-473207-16');

          gtag('config', 'UA-473207-16', {
            page_path: event.urlAfterRedirects,
          });
        }
      });

    // this.addGoogleMapsScript();
  }

  ngOnInit(): void {
    this.translate.addLangs([
      'en-EN',
      'fr-FR',
      'de-DE',
      'en-US',
      'es-ES',
      'it-IT',
      'nl-NL',
      'jp-JP',
    ]);
    this.translate.setDefaultLang(
      this.languageService.get() ? this.languageService.get() : 'fr-FR'
    );
    this.initializeStore();
    this.initializeCookieConsent();

    this.activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: Params) => {
        let token: string = params['token'];
        if (!token) {
          token = this.extractTokenFromHash();
        }

        if (token) {
          this.loginService.loginOauth(token);
        }
      });
  }

  get supportSafeLink(): string {
    let lang: string = this.languageService.get();
    if (lang === 'fr-FR') lang = 'fr';
    if (lang === 'de-DE') lang = 'de';
    if (!['de', 'fr', 'en-us'].includes(lang)) lang = 'en-us';
    return `<a href='https://support.kinomap.com/hc/${lang}' target='_blank'>FAQ</a>`;
  }

  toggleFakeZendeskMenu(): void {
    if (this.fakeZendeskMenu) {
      this.fakeZendeskMenuClosing = true;
      setTimeout(() => {
        this.fakeZendeskMenu = false;
        this.fakeZendeskMenuClosing = false;
      }, 250);
    } else {
      this.fakeZendeskMenu = true;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const fakeZendeskElement = target.closest('.fake-zendesk');

    // Fermer le menu si le clic est en dehors de .fake-zendesk et que le menu est ouvert
    if (!fakeZendeskElement && this.fakeZendeskMenu) {
      this.fakeZendeskMenuClosing = true;
      setTimeout(() => {
        this.fakeZendeskMenu = false;
        this.fakeZendeskMenuClosing = false;
      }, 250);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscription.forEach((element: Subscription): void =>
      element.unsubscribe()
    );
  }
}
